/**
 * Shared number formatting utilities for Reserve Data pages
 * Standardizes display of numbers, currencies, and percentages
 */

/**
 * Format a number with thousands separators
 * Shows max 2 decimal places, removes trailing zeros
 * @param {number|null|undefined} value - The number to format
 * @returns {string} Formatted number or '—' if invalid
 */
export function formatNumber(value) {
  if (value === null || value === undefined) return '—';

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

/**
 * Format a currency value with $ prefix and USD suffix
 * Shows max 2 decimal places, removes trailing zeros
 * @param {number|null|undefined} value - The currency value to format
 * @returns {string} Formatted currency or '—' if invalid
 */
export function formatCurrency(value) {
  if (value === null || value === undefined) return '—';

  return '$' + Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

/**
 * Format a currency value with $ prefix and fixed two decimal places
 * @param {number|null|undefined} value - The currency value to format
 * @returns {string} Formatted currency or '—' if invalid
 */
export function formatCurrencyFixed(value) {
  if (value === null || value === undefined) return '—';

  return '$' + Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/**
 * Format a currency value - returns just the number part (without $ or USD)
 * Used when USD needs to be styled separately
 * @param {number|null|undefined} value - The currency value to format
 * @returns {string} Formatted number or '—' if invalid
 */
export function formatCurrencyValue(value) {
  if (value === null || value === undefined) return '—';

  return '$' + Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

/**
 * Format a currency value with institutional shorthand (K / M / B)
 * Examples: 16234 -> "$16.2K", 1250000 -> "$1.25M", 985 -> "$985"
 * @param {number|null|undefined} value - The currency value to format
 * @returns {string} Formatted shorthand currency or '—' if invalid
 */
export function formatCurrencyShort(value) {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (!isFinite(num)) return '—';

  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  const trim = (n, digits) => {
    const fixed = n.toFixed(digits);
    return parseFloat(fixed).toString();
  };

  if (abs >= 1.0e9) return `${sign}$${trim(abs / 1.0e9, 2)}B`;
  if (abs >= 1.0e6) return `${sign}$${trim(abs / 1.0e6, 2)}M`;
  if (abs >= 1.0e3) return `${sign}$${trim(abs / 1.0e3, 1)}K`;
  return `${sign}$${trim(abs, 2)}`;
}

/**
 * Format a number value with institutional shorthand (K / M / B)
 * Examples: 16234 -> "16.2K", 1250000 -> "1.25M"
 * @param {number|null|undefined} value - The number to format
 * @returns {string} Formatted shorthand number or '—' if invalid
 */
export function formatNumberShort(value) {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (!isFinite(num)) return '—';

  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  const trim = (n, digits) => parseFloat(n.toFixed(digits)).toString();

  if (abs >= 1.0e9) return `${sign}${trim(abs / 1.0e9, 2)}B`;
  if (abs >= 1.0e6) return `${sign}${trim(abs / 1.0e6, 2)}M`;
  if (abs >= 1.0e3) return `${sign}${trim(abs / 1.0e3, 1)}K`;
  return `${sign}${trim(abs, 2)}`;
}

/**
 * Format a percentage value with % suffix
 * Shows max 2 decimal places, removes trailing zeros
 * @param {number|null|undefined} value - The percentage value to format
 * @returns {string} Formatted percentage or '—' if invalid
 */
export function formatPercentage(value) {
  if (value === null || value === undefined) return '—';

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}
